import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { toast } from "sonner";

import MainContainer from "../../components/MainContainer";
import CollectionItem from "../../components/utils/CollectionItem/CollectionItem";
import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import ExplosionButton from "../../components/ExplosionButton/ExplosionButton";
import GenerationToast from "../../components/utils/GenerationToast";
import CreateImageSettingsModal from "../../components/CreateImageModal/CreateImageSettingsModal";
import CollectionModal from "../../components/CollectionModal/CollectionModal";
import Pagination from "../../components/Pagination/Pagination";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { ReactComponent as CreditIcon } from "../../assets/icons/credit.svg";

import settingsAnimation from "../../assets/animations/settings.json";
import checkboxAnimation from "../../assets/animations/radioButton.json";

import {
  fetchCollection,
  generateImageCollection,
  getCollectionsByModelId,
} from "../../store/slices/model/asyncThunks";
import {
  selectCollectionDetails,
  selectGenerateImageCollectionPending,
  selectModelCollections,
  selectSelectedCollection,
  setSelectedCollection,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { setSignUpModalOpen } from "../../store/slices/common/slice";

import { formatModelName, isImageOutput } from "../../utils/utils";
import {
  COLLECTION_STATUS,
  CREATE_IMAGE_MODEL_NAME,
} from "../../utils/constants";

import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import EditImageModal from "../../components/EditImageModal/EditImageModal";

import {
  BackButton,
  Title,
  TitleContainer,
  TitleRow,
  Content,
  FormCard,
  Column,
  LabelRow,
  LabelText,
  InfoIconWrapper,
  TooltipContainer,
  TooltipText,
  Textarea,
  CheckboxContainerColumn,
  CheckboxContainer,
  CheckboxText,
  AspectRatioContainer,
  AspectRatioOption,
  AspectRatioPreviewWrapper,
  AspectRatioPreview,
  AspectRatioLabel,
  GenerateRow,
  SettingsIconWrapper,
  CreditBadge,
  SectionTitle,
  TabContentSection,
  MasonryGrid,
  MasonryColumn,
  EmptyState,
  SkeletonBox,
  PaginationWrapper,
} from "./CreateImageDetailsPage.styles";

const ASPECT_RATIOS = [
  { id: "1:1", label: "1:1", width: 22, height: 22 },
  { id: "2:3", label: "2:3", width: 14, height: 22 },
  { id: "3:2", label: "3:2", width: 22, height: 14 },
  { id: "9:16", label: "9:16", width: 12, height: 22 },
  { id: "16:9", label: "16:9", width: 22, height: 12 },
];

const CREATE_IMAGE_COLLECTION_ID = "698b063fde9760a1b96cd8fc";

const generateRandomSeed = () => Math.floor(Math.random() * 10000000) + 1;

const COLLECTION_POLL_INTERVAL = 30000;

const CreateImageDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;

  const tooltipRef = useRef(null);
  const collectionSectionRef = useRef(null);

  const { data: collectionDetailsData, loading } = useSelector(
    selectCollectionDetails,
  );
  const {
    data: modelCollectionsData,
    loading: modelCollectionsLoading,
    total: modelCollectionsTotal,
    page: modelCollectionsCurrentPage,
    modelId: modelCollectionsCurrentModelId,
  } = useSelector(selectModelCollections);
  const generateLoading = useSelector(selectGenerateImageCollectionPending);
  const selectedCollection = useSelector(selectSelectedCollection);
  const loggedIn = useSelector(selectLoggedIn);

  const previewCollection = location.state?.collection;
  const playlistId = location.state?.playlistId;
  const fetchedCollection =
    collectionDetailsData?._id === CREATE_IMAGE_COLLECTION_ID
      ? collectionDetailsData
      : null;

  const data = useMemo(() => {
    if (!previewCollection) return fetchedCollection ?? null;
    if (!fetchedCollection) return previewCollection;
    return { ...previewCollection, ...fetchedCollection };
  }, [previewCollection, fetchedCollection]);

  const isInitialLoading = loading && !data;

  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState(
    () => localStorage.getItem("createImageAspectRatio") || "1:1",
  );
  const [negativePrompt, setNegativePrompt] = useState("");
  const [quality, setQuality] = useState(
    () => localStorage.getItem("createImageQuality") || "LOW",
  );
  const [shouldEnhancePrompt, setShouldEnhancePrompt] = useState(false);
  const [seedNumber, setSeedNumber] = useState(generateRandomSeed());
  const [isExploding, setIsExploding] = useState(false);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isDesktop = useMediaQuery({ maxWidth: 1224 });

  const numOfColumns = useMemo(() => {
    if (isMobile) return 2;
    if (isTable) return 3;
    if (isDesktop) return 4;
    return 5;
  }, [isMobile, isTable, isDesktop]);

  const basePrice = quality === "HIGH" ? 15 : 10;
  const totalPrice = basePrice + (shouldEnhancePrompt ? 5 : 0);

  // Fetch collection by id on mount / id change
  useEffect(() => {
    dispatch(fetchCollection({ collectionId: CREATE_IMAGE_COLLECTION_ID }));
  }, [dispatch]);

  // Fetch related collections by model when we know the model
  useEffect(() => {
    if (data?.model) {
      dispatch(getCollectionsByModelId({ modelId: data.model, page }));
    }
  }, [dispatch, data?.model, page]);

  const hasInProgressCollections = useMemo(
    () =>
      Array.isArray(modelCollectionsData) &&
      modelCollectionsData.some(
        (collection) =>
          collection?.status === COLLECTION_STATUS.IN_PROGRESS ||
          collection?.status === COLLECTION_STATUS.NUDIFY_IN_PROGRESS ||
          collection?.status === COLLECTION_STATUS.UPSCALE_IN_PROGRESS ||
          collection?.status === COLLECTION_STATUS.AUDIO_IN_PROGRESS,
      ),
    [modelCollectionsData],
  );

  // Poll while there are in-progress collections (only on first page)
  useEffect(() => {
    if (!data?.model || !hasInProgressCollections || page !== 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      dispatch(getCollectionsByModelId({ modelId: data.model, page }));
    }, COLLECTION_POLL_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, data?.model, hasInProgressCollections, page]);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showTooltip]);

  const handleBack = () => {
    navigate(-1);
  };

  const setPage = (nextPage) => {
    searchParams.set("page", nextPage);
    setSearchParams(searchParams);
    if (collectionSectionRef.current) {
      const rect = collectionSectionRef.current.getBoundingClientRect();
      const top = rect.top + window.scrollY - 16;
      window.scrollTo({ top, left: 0, behavior: "smooth" });
    }
  };

  const resetStates = () => {
    setPrompt("");
    setNegativePrompt("");
    setShowTooltip(false);
  };

  const handleGenerate = async () => {
    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }
    if (generateLoading || !data) return;

    if (!prompt || !prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    const formData = new FormData();
    formData.append("customPrompt", prompt.trim());
    formData.append("aspectRatio", aspectRatio);
    formData.append("modelId", data?.model);
    formData.append("quality", quality);
    formData.append("shouldEnhancePrompt", shouldEnhancePrompt);

    if (negativePrompt && negativePrompt.trim()) {
      formData.append("negativePrompt", negativePrompt.trim());
    }

    formData.append("seed", seedNumber || generateRandomSeed());

    if (playlistId) {
      formData.append("playlistId", playlistId);
    }

    dispatch(generateImageCollection(formData))
      .unwrap()
      .then(() => {
        toast.custom(() => (
          <GenerationToast
            successMessage="Generation started..."
            label="View"
            onClick={() => {
              navigate("/collection");
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          />
        ));

        setIsExploding(true);
        setTimeout(() => {
          setIsExploding(false);
          resetStates();
        }, 500);
      })
      .catch(() => {
        toast.error(
          "An error occurred while generating images. Please try again later.",
        );
      });
  };

  const handleCollectionItemClick = (item) => {
    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }

    dispatch(setSelectedCollection(item));

    if (item.modelType === CREATE_IMAGE_MODEL_NAME) {
      setIsCollectionModalOpen(true);
    } else if (isImageOutput(item.modelType ?? item.modelName)) {
      setIsEditImageModalOpen(true);
    } else {
      setIsGenerateCollectionModalOpen(true);
    }
  };

  const isCollectionsTransitioning =
    modelCollectionsLoading &&
    (page !== modelCollectionsCurrentPage ||
      (data?.model && modelCollectionsCurrentModelId !== data.model));

  const renderCollectionGrid = () => {
    if (
      isCollectionsTransitioning ||
      (modelCollectionsLoading && !modelCollectionsData?.length)
    ) {
      return <VideoMasonrySkeletonLoader count={5} isCollectionPage />;
    }

    if (!modelCollectionsData || !modelCollectionsData.length) {
      return (
        <EmptyState>
          <h3>Nothing found</h3>
          <p>There are no collections to display at the moment.</p>
        </EmptyState>
      );
    }

    return (
      <MasonryGrid>
        {Array.from({ length: numOfColumns }).map((_, colIndex) => (
          <MasonryColumn key={colIndex}>
            {modelCollectionsData
              .filter((_, i) => i % numOfColumns === colIndex)
              .map((item, rowIndex) => (
                <CollectionItem
                  key={item._id}
                  video={item}
                  priority={rowIndex < 4}
                  onCollectionItemClick={() => handleCollectionItemClick(item)}
                />
              ))}
          </MasonryColumn>
        ))}
      </MasonryGrid>
    );
  };

  return (
    <MainContainer>
      <TitleContainer>
        <TitleRow>
          <BackButton onClick={handleBack}>
            <ChevronLeft />
            Back
          </BackButton>
        </TitleRow>

        {isInitialLoading || !data ? (
          <SkeletonBox $width="200px" $height="39px" $radius="10px" />
        ) : (
          <Title>
            {data?.name ? formatModelName(data.name) : "Create Image"}
          </Title>
        )}
      </TitleContainer>

      <Content>
        <FormCard>
          <Column>
            <LabelRow>
              <LabelText>Prompt</LabelText>
              <InfoIconWrapper
                ref={tooltipRef}
                $active={!!data?.customPrompt}
                onClick={() => {
                  if (data?.customPrompt) {
                    setShowTooltip(!showTooltip);
                  }
                }}
              >
                <InfoIcon />
                {showTooltip && data?.customPrompt && (
                  <TooltipContainer>
                    <TooltipText>{data.customPrompt}</TooltipText>
                  </TooltipContainer>
                )}
              </InfoIconWrapper>
            </LabelRow>
            <Textarea
              placeholder="Enter prompt here"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={generateLoading}
            />
          </Column>

          <CheckboxContainerColumn>
            <CheckboxContainer
              onClick={() => setShouldEnhancePrompt(!shouldEnhancePrompt)}
            >
              <LottieIcon
                animationData={checkboxAnimation}
                trigger="toggle-reverse"
                isActive={shouldEnhancePrompt}
                speed={2}
                onActiveChange={() =>
                  setShouldEnhancePrompt(!shouldEnhancePrompt)
                }
                onClick={() => setShouldEnhancePrompt(!shouldEnhancePrompt)}
                size={24}
                strokeColor={"#2877d9"}
              />
              <CheckboxText>Enhance prompt before generation</CheckboxText>
            </CheckboxContainer>
          </CheckboxContainerColumn>

          <Column>
            <LabelText>Aspect Ratio</LabelText>
            <AspectRatioContainer>
              {ASPECT_RATIOS.map((ratio) => {
                const selected = aspectRatio === ratio.id;
                return (
                  <AspectRatioOption
                    key={ratio.id}
                    $selected={selected}
                    onClick={() => {
                      if (!generateLoading) {
                        setAspectRatio(ratio.id);
                        localStorage.setItem(
                          "createImageAspectRatio",
                          ratio.id,
                        );
                      }
                    }}
                  >
                    <AspectRatioPreviewWrapper>
                      <AspectRatioPreview
                        $selected={selected}
                        $width={ratio.width}
                        $height={ratio.height}
                      />
                    </AspectRatioPreviewWrapper>
                    <AspectRatioLabel>{ratio.label}</AspectRatioLabel>
                  </AspectRatioOption>
                );
              })}
            </AspectRatioContainer>
          </Column>

          <GenerateRow>
            <SettingsIconWrapper
              onClick={() => !generateLoading && setIsCustomizeOpen(true)}
            >
              <LottieIcon
                animationData={settingsAnimation}
                trigger="hover"
                strokeColor={"#fff"}
                size={28}
              />
            </SettingsIconWrapper>
            <ExplosionButton
              style={{ flex: 1 }}
              disabled={generateLoading}
              onClick={handleGenerate}
              isLoading={generateLoading}
              isExploding={isExploding}
              small
            >
              {generateLoading ? (
                <l-line-spinner
                  size="16"
                  stroke="1.25"
                  speed="1.5"
                  color="black"
                />
              ) : (
                <>
                  Generate
                  <CreditIcon />
                  <CreditBadge>{totalPrice}</CreditBadge>
                </>
              )}
            </ExplosionButton>
          </GenerateRow>
        </FormCard>

        <TabContentSection ref={collectionSectionRef}>
          <SectionTitle>Collections</SectionTitle>
          {renderCollectionGrid()}
          <PaginationWrapper>
            <Pagination
              currentPage={page}
              totalCount={modelCollectionsTotal}
              perPage={30}
              onPageChange={(nextPage) => setPage(nextPage)}
            />
          </PaginationWrapper>
        </TabContentSection>
      </Content>

      <CreateImageSettingsModal
        isOpen={isCustomizeOpen}
        onClose={() => setIsCustomizeOpen(false)}
        seedNumber={seedNumber}
        setSeedNumber={setSeedNumber}
        negativePrompt={negativePrompt}
        setNegativePrompt={setNegativePrompt}
        quality={quality}
        setQuality={setQuality}
        totalPrice={totalPrice}
        collectionSeed={data?.seed}
        overlayZIndex={3500}
        resetSeed={isCustomizeOpen}
      />

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollection}
        onClose={() => setIsEditImageModalOpen(false)}
      />

      <CollectionModal
        isOpen={isCollectionModalOpen}
        onClose={() => setIsCollectionModalOpen(false)}
        video={selectedCollection}
        rating={selectedCollection?.rating ?? 0}
      />
    </MainContainer>
  );
};

export default CreateImageDetailsPage;
